import { useCallback, useSyncExternalStore } from 'react';
import { issuePortalLink, type ApiError, type PortalLink } from '@/lib/api';

/**
 * ONE ISSUED LINK PER PERSON, shared by every component showing that person.
 *
 * WHY THIS IS NOT `useState`. It was, and the bug Julio flagged on #466 is this: issuing a link
 * REVOKES whatever link the person already had, and `SectionWorkspace` keeps a section mounted once
 * it has been visited. So step 2's "Hand over the link" and step 3's Portal panel are both live at
 * once on the same record, each holding its own copy. Issue from step 3 and step 2 goes on
 * displaying a link and a code that stopped working — with the reveal's own "copy both now" notice
 * above them, which is the worst possible version of this: HR hands over a dead credential while
 * the screen insists it is the only copy.
 *
 * WHY NOT THE REACT-QUERY CACHE either, which is the obvious shared place. A mutation's result
 * lives in the mutation cache, readable by anything holding the client and kept for minutes after
 * the component is done with it. That is a bearer credential sitting somewhere nobody is looking.
 * `tests/offer-qa-fixes.test.tsx` N1 pins its absence from both that cache and browser storage.
 *
 * SO: a module store, keyed by person, whose entry is DROPPED when the last component showing that
 * person goes away. The sharing is the fix; the refcount is what keeps the old security property —
 * the credential still does not outlive the UI that revealed it, it just now outlives one component
 * of it rather than dying with whichever one unmounted first.
 */

export interface PortalLinkIssueState {
  /** The link and code, shown once. Null before anything is issued and after `clear`. */
  readonly issued: PortalLink | null;
  readonly pending: boolean;
  readonly errorMessage: string | null;
}

/** The shared "nothing yet" value. One object, so `getSnapshot` is referentially stable. */
const NOTHING: PortalLinkIssueState = { issued: null, pending: false, errorMessage: null };

interface Entry {
  state: PortalLinkIssueState;
  /**
   * The subscribed components. Its SIZE is the consumer count — there is no separate counter that
   * could drift out of step with the set it is meant to describe.
   */
  readonly listeners: Set<() => void>;
}

const live = new Map<string, Entry>();

function entryFor(personId: string): Entry {
  const existing = live.get(personId);
  if (existing !== undefined) return existing;
  const created: Entry = { state: NOTHING, listeners: new Set() };
  live.set(personId, created);
  return created;
}

const snapshot = (personId: string): PortalLinkIssueState => live.get(personId)?.state ?? NOTHING;

function set(personId: string, state: PortalLinkIssueState): void {
  const entry = live.get(personId);
  /*
   * No entry means every consumer went away while the request was in flight. The answer is
   * DISCARDED rather than stored for a future mount: a link nobody asked to see is a credential
   * with no reader. It cost the person their previous link, which is unavoidable once the POST is
   * sent, and the remedy is the same as for any lost code — issue another one.
   */
  if (entry === undefined) return;
  entry.state = state;
  // A copy, because a listener may unsubscribe while being notified.
  for (const listener of [...entry.listeners]) listener();
}

/**
 * A consumer has gone; drop the credential if it was the last one for this person.
 *
 * ON THE ENTRY IT SUBSCRIBED TO rather than on whatever the map holds now: StrictMode unsubscribes
 * and resubscribes on every mount, so a cleanup running against a replaced entry is a thing that
 * can happen, and given its own entry the worst it can touch is an object nobody else reads.
 *
 * The `live.get(...) === entry` clause is A GUARD WITH NO TEST BEHIND IT, said plainly rather than
 * left to look pinned. It stops a stale cleanup deleting a replacement entry that still has live
 * readers, and I could not construct that interleaving through React: subscribe, cleanup and the
 * next subscribe run in that order, StrictMode included, so the map's entry is never swapped while
 * a listener survives on the old one. It stays because it makes this total rather than correct only
 * while that ordering holds.
 */
function release(personId: string, entry: Entry): void {
  if (entry.listeners.size === 0 && live.get(personId) === entry) live.delete(personId);
}

/**
 * Issue a portal link for one person, and read the one link that person has.
 *
 * `onIssued` takes NO argument, deliberately: a caller needs to know the link CHANGED — step 3's
 * status block is stale the instant this succeeds — not what it is. Handing the link to a callback
 * is how it ends up somewhere this module cannot account for.
 */
export function usePortalLinkIssue(personId: string, onIssued?: () => void) {
  const subscribe = useCallback((notify: () => void) => {
    const entry = entryFor(personId);
    entry.listeners.add(notify);
    return () => { entry.listeners.delete(notify); release(personId, entry); };
  }, [personId]);

  const state = useSyncExternalStore(subscribe, () => snapshot(personId), () => NOTHING);

  const issue = () => {
    // One request is enough. A second while the first is in flight mints a link that revokes it,
    // and both components are showing the same pending state, so the click is genuinely a repeat.
    if (snapshot(personId).pending) return;
    set(personId, { ...snapshot(personId), pending: true, errorMessage: null });
    issuePortalLink(personId)
      .then((link) => { set(personId, { issued: link, pending: false, errorMessage: null }); onIssued?.(); })
      // The previous link is KEPT on failure: nothing was issued, so nothing was revoked.
      .catch((failure: Error & Partial<ApiError>) => set(personId, { ...snapshot(personId), pending: false, errorMessage: failure.message }));
  };

  const clear = () => set(personId, { ...snapshot(personId), issued: null, errorMessage: null });

  return { ...state, issue, clear };
}
